import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Send, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { TextInputField, TextAreaField } from '../form/FormFields';
import { clinicInfo } from '../../mock';

// Success prompt component shown after form submission
const IntakePrompt = ({ onNavigate, onDismiss }) => (
  <Card className="mb-8 border-2 border-green-400 shadow-xl" data-testid="intake-prompt">
    <CardContent className="p-6 flex flex-col md:flex-row items-center gap-4">
      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
        <CheckCircle className="text-green-600" size={28} />
      </div>
      <div className="flex-1 text-center md:text-left">
        <h3 className="text-lg font-bold text-darkBrown">Message Sent Successfully!</h3>
        <p className="text-gray-600 text-sm">Are you a new patient? Speed up your first visit by completing our intake form now.</p>
      </div>
      <div className="flex gap-2">
        <Button
          data-testid="intake-prompt-btn"
          onClick={onNavigate}
          className="bg-primaryOrange hover:bg-accentOrange text-white"
        >
          <FileText size={16} className="mr-2" /> Complete Intake Form
        </Button>
        <Button variant="outline" onClick={onDismiss} className="border-gray-300">
          Dismiss
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Contact info card component
const ContactInfoCard = ({ icon: Icon, label, content, subContent }) => (
  <Card className="border-none shadow-lg bg-warmBeige text-center">
    <CardContent className="pt-6">
      <div className="w-12 h-12 bg-primaryOrange rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="text-white" size={24} />
      </div>
      <p className="text-sm text-gray-600 mb-2">{label}</p>
      <p className="font-semibold text-darkBrown text-sm">{content}</p>
      {subContent && <p className="text-xs text-gray-500 mt-1">{subContent}</p>}
    </CardContent>
  </Card>
);

export const ContactForm = ({ formData, handleInputChange, handleSubmit: parentSubmit }) => {
  const navigate = useNavigate();
  const [showIntakePrompt, setShowIntakePrompt] = useState(false);

  const handleSubmit = async (e) => {
    const hadData = formData.name && formData.email && formData.message;
    await parentSubmit(e);
    if (hadData) {
      setShowIntakePrompt(true);
    }
  };

  return (
    <section id="contact" data-testid="contact-form-section" className="fade-in-section py-20 px-4 bg-white/80 backdrop-blur-sm relative z-10">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-darkBrown mb-4">Get In Touch</h2>
          <p className="text-xl text-gray-600">Turn obstacles and challenges into opportunities to grow</p>
          <div className="w-24 h-1 bg-primaryOrange mx-auto mt-4"></div>
        </div>

        {/* Intake Form Prompt */}
        {showIntakePrompt && (
          <IntakePrompt 
            onNavigate={() => navigate('/intake')} 
            onDismiss={() => setShowIntakePrompt(false)} 
          />
        )}

        {/* Contact Form */}
        <Card className="shadow-2xl border-none">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <TextInputField
                  label="Full Name"
                  required
                  testId="contact-name-input"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                />
                <TextInputField
                  label="Email"
                  required
                  testId="contact-email-input"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                />
              </div>
              <TextInputField
                label="Phone Number"
                testId="contact-phone-input"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
              />
              <div className="grid md:grid-cols-2 gap-6">
                <TextInputField
                  label="Preferred Appointment Date"
                  testId="contact-preferred-date-input"
                  name="preferredDate"
                  type="date"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                />
                <TextInputField
                  label="Preferred Appointment Time"
                  testId="contact-preferred-time-input"
                  name="preferredTime"
                  type="time"
                  value={formData.preferredTime}
                  onChange={handleInputChange}
                />
              </div>
              <TextAreaField
                label="Message"
                required
                testId="contact-message-input"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us how we can help you..."
              />
              <Button
                data-testid="contact-submit-btn"
                type="submit"
                size="lg"
                className="w-full bg-primaryOrange hover:bg-accentOrange text-white shadow-lg hover:shadow-xl transition-all"
              >
                <Send className="mr-2" size={18} />
                Send Message
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <ContactInfoCard 
            icon={Phone} 
            label="Call/Text" 
            content={clinicInfo.phone}
            subContent={`Fax: ${clinicInfo.fax}`}
          />
          <ContactInfoCard 
            icon={Mail} 
            label="Email Us" 
            content={clinicInfo.email}
          />
          <ContactInfoCard 
            icon={MapPin} 
            label="Visit Us" 
            content={clinicInfo.address}
          />
        </div>
      </div>
    </section>
  );
};
